import styled from 'styled-components'

import { DomUtil } from '@root/shared/core'
import { useTranslation } from '@root/shared/localization'
import { LogoGlass, type IconProps } from '@root/shared/ui-kit'

import DIAMETERS_BG from '../../assets/diameters.inline.svg'

type AuthModalInfoProps = {
  icon?: React.FC<IconProps>
  children: React.ReactNode
}

export const AuthModalInfo = ({ icon, children }: AuthModalInfoProps) => {
  const Icon = icon
  const { t } = useTranslation()

  return (
    <Root>
      <Body>
        <Logo>
          <LogoGlass size={50} />
        </Logo>
        <Group>
          <Title>{t('app.name')}</Title>
          <Pitch>{t('app.pitch')}</Pitch>
        </Group>
        <SideMessage>
          {Icon ? <Icon size={30} /> : undefined}
          <Content>{children}</Content>
        </SideMessage>
        <DiametersBg />
      </Body>
    </Root>
  )
}

const Root = styled.div`
  padding: 7px;
`

const Body = styled.div`
  background-color: var(--background-1);
  border-radius: 28px;
  opacity: 0.95;
  width: 209px;
  min-height: 320px;
  height: 100%;
  overflow: hidden;

  display: grid;
  grid-template-rows: 100px auto;

  align-content: space-between;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.3;

    background-image: radial-gradient(
      100% 80% at 20% 0%,
      var(--primary-5) 20%,
      var(--primary-9) 70%,
      transparent 100%
    );
  }
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    opacity: 0.4;
    background: radial-gradient(
      90% 80% at 80% 40%,
      var(--primary-9) 0%,
      rgba(var(--primary-9-rgb), 0.56) 50%,
      transparent 100%
    );
  }

  position: relative;
`

const DiametersBg = styled.div`
  background: ${DomUtil.cssUrl(DIAMETERS_BG)};

  background-size: cover;
  background-repeat: no-repeat;
  width: 250px;
  height: 250px;

  position: absolute;
  top: -15px;
  left: -28px;
`

const SideMessage = styled.div`
  min-height: 97px;
  padding-block: 14px;
  border-radius: 20px;
  opacity: 0.95;
  background: var(--backgroundA-3);
  margin: 10px;
  gap: 6px;

  padding-inline: 16px;
  display: grid;
  align-content: center;
  justify-content: center;
  justify-items: center;

  > svg {
    color: var(--text-12);
  }
`

const Content = styled.div`
  color: var(--textA-10);
  text-align: center;
  font-size: 10px;
  font-weight: 500;
  line-height: 14px;
`

const Group = styled.div`
  display: grid;
  grid-template-rows: auto auto;
  gap: 5px;
  text-align: center;
  z-index: 1;
`

const Title = styled.h1`
  color: var(--text-12);
  font-size: 20px;
  font-weight: 600;
`

const Pitch = styled.h2`
  color: var(--textA-10);
  text-align: center;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`

const Logo = styled.header`
  display: grid;
  grid-template-rows: auto auto;
  height: 100%;
  gap: 18px;
  place-items: center;
  align-items: start;
  z-index: 1;
  align-content: start;
  margin-top: 50px;
`
